import type { StorefrontTheme } from "@loot-os/api-contract";
import { type CSSProperties, type ReactNode, useLayoutEffect, useMemo } from "react";

import { applyTheme, THEME_ROOT_ATTRIBUTE } from "./applyTheme.js";
import { loadFonts } from "./fonts.js";
import { ThemeSpecContext } from "./themeContext.js";
import { resolveThemeVars } from "./themeVars.js";

export interface ThemeProviderProps {
  theme?: StorefrontTheme | null;
  /**
   * `document` themes the whole page (the storefront, Storybook); `scoped` themes only a box (a
   * dashboard preview). Popups opened from a scoped box render outside it, in the page's theme.
   */
  target?: "document" | "scoped";
  children: ReactNode;
  className?: string;
}

/** Applies a theme and gives the components below it its layout and stage choices. */
export function ThemeProvider({
  theme,
  target = "document",
  children,
  className,
}: ThemeProviderProps) {
  const vars = useMemo(() => resolveThemeVars(theme), [theme]);
  useLayoutEffect(() => {
    if (target === "document") applyTheme(document.documentElement, theme);
    else void loadFonts(vars.fonts);
  }, [target, theme, vars]);
  const content = (
    <ThemeSpecContext.Provider value={vars.spec}>{children}</ThemeSpecContext.Provider>
  );
  if (target === "document") return content;
  return (
    <div
      {...{ [THEME_ROOT_ATTRIBUTE]: "" }}
      className={className}
      style={
        {
          ...vars.properties,
          fontFamily: vars.fontFamily,
          colorScheme: vars.colorScheme ?? undefined,
        } as CSSProperties
      }
    >
      {content}
    </div>
  );
}
